// Helpers for reading the room-capture screen's React Router
// navigation state. Extracted from room-capture-screen.tsx so the
// page stays under the 500-line lint cap.
//
// State doesn't survive a hard reload — by then the local PowerSync
// mirror has caught up and the regular session.spaces lookup
// succeeds, so the screen no longer needs the seeded fallback.

import type { SessionDetail, SpaceWithItems } from '@citycare/shared'

export interface LocationNavState {
  sessionId?: string
  // Optional pre-seeded space carried from the building screen when
  // the user has just created a new room. Without it the room screen
  // briefly renders "Room not found" because the local PowerSync
  // mirror hasn't replicated the new row yet.
  space?: SpaceWithItems
}

export function readSessionIdFromLocationState(state: unknown): string | null {
  if (state === null || typeof state !== 'object') return null
  const candidate = (state as LocationNavState).sessionId
  if (typeof candidate !== 'string' || candidate === '') return null
  return candidate
}

export function readSpaceFromLocationState(
  state: unknown,
): SpaceWithItems | null {
  if (state === null || typeof state !== 'object') return null
  const candidate = (state as LocationNavState).space
  if (
    candidate === undefined ||
    candidate === null ||
    typeof candidate !== 'object'
  ) {
    return null
  }
  // Minimal shape check — the nav state is shaped by the same React
  // tree, so we trust it. If a future caller passes something stray,
  // we'd rather render the standard "Room not found" than crash.
  if (typeof (candidate as { id?: unknown }).id !== 'string') return null
  return candidate
}

// Seed a nav-forwarded space into the session detail's spaces array
// when it isn't already present. Used at local-mirror seed time for
// the just-created-room race: PowerSync hasn't replicated the new
// space row yet, so `detail.spaces` is missing it. Without this seed
// the optimistic update / reconcile paths (which key on `spaceId` in
// `prev.spaces`) silently drop counter taps until the screen
// remounts. Idempotent on repeat calls — once `detail.spaces` carries
// a row with the same id, the navSpace is ignored.
export function mergeNavSpaceIntoSession(
  detail: SessionDetail,
  navSpace: SpaceWithItems | null,
): SessionDetail {
  if (navSpace === null) return detail
  if (detail.spaces.some((s) => s.id === navSpace.id)) return detail
  return { ...detail, spaces: [navSpace, ...detail.spaces] }
}
